/** Ana sayfa düzeni: şema başına bölüm kataloğu ve panel kaydının çözümü.
 *
 * Her şemanın ana sayfası kendi bölümlerini çizer ve hangi şemada hangi
 * bölümün bulunduğu **buradadır** — bölümü çizen istemcidir, backend yalnızca
 * yöneticinin kararını tutar (`services/home_layout.py`). Liste iki yerden
 * okunur: ana sayfanın kendisi (`components/HomeBlocks.jsx`) ve panelin
 * **Ana Sayfa Düzeni** ekranı. Ayrışsalardı panelde görünen bir bölüm sitede
 * yer değiştirmezdi.
 *
 * Anahtarlar `lib/domHooks.js` `SECTIONS` adlarıdır: panelde sırası değişen
 * bölüm, CSS'te `[data-bhs-section="…"]` ile seçilen bölümün kendisidir.
 *
 * **Yeni bir bölüm eklerken** şemanın listesine varsayılan yerinde yazın ve
 * ana sayfada `<HomeBlock id="…">` ile sarın. Eski kayıtlar onu gizlemez:
 * kayıtta bulunmayan bölüm varsayılan yerinde eklenir.
 */
import { SECTIONS } from './domHooks';

export const HOME_BLOCK_LABELS = {
  [SECTIONS.HERO]: 'Banner',
  [SECTIONS.CATEGORIES]: 'Kategori kartları',
  [SECTIONS.SHORTCUTS]: 'Kısayollar',
  [SECTIONS.PICKS]: 'Size Özel Seçtiklerimiz',
  [SECTIONS.POPULAR]: 'Popüler oyunlar',
  [SECTIONS.SLOTS]: 'Slot oyunları',
  [SECTIONS.LIVE_CASINO]: 'Canlı casino',
  [SECTIONS.NEW]: 'Yeni oyunlar',
  [SECTIONS.FAST]: 'Hızlı oyunlar',
  [SECTIONS.MATCHES]: 'Maçlar',
  [SECTIONS.WINS]: 'Kazananlar',
  [SECTIONS.PROVIDERS]: 'Sağlayıcılar',
  [SECTIONS.BONUSES]: 'Bonuslar',
  [SECTIONS.FEATURED]: 'Öne çıkan kampanya',
  [SECTIONS.LIVE_BETS]: 'Canlı bahisler',
  [SECTIONS.FAQ]: 'Sıkça sorulan sorular',
};

// Bölümün ne olduğunu panelde söyleyen kısa açıklama. Aynı ad şemadan şemaya
// farklı başlıkla çizilir ("Trend", "En Popüler"); yönetici sitede gördüğü
// başlığı burada da tanıyabilmelidir.
export const HOME_BLOCK_HINTS = {
  [SECTIONS.HERO]: 'Üstteki banner / slider alanı',
  [SECTIONS.CATEGORIES]: 'Slot, Canlı Casino, Spor … kartları',
  [SECTIONS.SHORTCUTS]: 'Banner altındaki kısayol şeridi',
  [SECTIONS.PICKS]: 'Oyun Yönetimi → Ana Sayfa Vitrini seçimi',
  [SECTIONS.POPULAR]: '"Popüler", "Trend", "En Popüler" şeridi',
  [SECTIONS.MATCHES]: 'Günün / öne çıkan maçlar',
  [SECTIONS.WINS]: 'Son / yüksek kazançlar şeridi',
  [SECTIONS.FEATURED]: 'Bonus kampanyası kartı',
  [SECTIONS.FAQ]: 'Banner & Kategori Kartları → SSS',
};

const S = SECTIONS;

/** Şema → varsayılan sırayla bölüm anahtarları (sitede bugün çizilen sıra). */
export const HOME_BLOCKS = {
  default: [S.HERO, S.CATEGORIES, S.NEW, S.LIVE_CASINO, S.POPULAR, S.MATCHES],
  merit: [S.HERO, S.CATEGORIES, S.PICKS, S.POPULAR, S.LIVE_CASINO, S.NEW, S.FAST,
    S.PROVIDERS, S.BONUSES],
  betrun: [S.HERO, S.CATEGORIES, S.PICKS, S.POPULAR, S.LIVE_CASINO, S.MATCHES,
    S.PROVIDERS, S.BONUSES],
  zumrut: [S.HERO, S.CATEGORIES, S.SHORTCUTS, S.PICKS, S.POPULAR, S.LIVE_CASINO,
    S.NEW, S.FAST, S.PROVIDERS, S.BONUSES],
  paris: [S.HERO, S.SHORTCUTS, S.CATEGORIES, S.POPULAR, S.SLOTS, S.LIVE_CASINO,
    S.NEW, S.FAST, S.PROVIDERS],
  vega: [S.HERO, S.CATEGORIES, S.SHORTCUTS, S.POPULAR, S.SLOTS, S.LIVE_CASINO,
    S.NEW, S.FAST, S.WINS, S.PROVIDERS],
  herkul: [S.HERO, S.WINS, S.POPULAR, S.BONUSES, S.LIVE_CASINO, S.NEW, S.FAST,
    S.PROVIDERS, S.FEATURED, S.LIVE_BETS, S.FAQ],
};

export const homeBlockCatalog = (theme) => HOME_BLOCKS[theme] || HOME_BLOCKS.default;

/** Kayıt + katalog → `[{key, visible}]`, sitede çizilecek sırayla.
 *
 * * Katalogda olmayan anahtar atlanır (kaldırılmış ya da başka şemanın
 *   bölümü): kayıt bozuk olsa da ana sayfa çizilmelidir.
 * * Kayıtta olmayan bölüm **görünür** ve varsayılan yerinde eklenir:
 *   varsayılan sırada kendisinden önce gelen en yakın bölümün hemen arkasına.
 *   Sona eklemek, yeni bir banner alanını sayfanın dibine atardı.
 */
export const resolveHomeLayout = (theme, saved) => {
  const catalog = homeBlockCatalog(theme);
  const known = new Set(catalog);
  const out = [];
  const seen = new Set();
  (Array.isArray(saved) ? saved : []).forEach((item) => {
    const key = String(item?.key || '');
    if (!known.has(key) || seen.has(key)) return;
    seen.add(key);
    out.push({ key, visible: item.visible !== false });
  });
  catalog.forEach((key, index) => {
    if (seen.has(key)) return;
    let at = 0;
    for (let i = index - 1; i >= 0; i -= 1) {
      const position = out.findIndex((item) => item.key === catalog[i]);
      if (position >= 0) { at = position + 1; break; }
    }
    out.splice(at, 0, { key, visible: true });
    seen.add(key);
  });
  return out;
};

/** Sitede çizilecek bölümlerin anahtarları, sırasıyla. */
export const visibleHomeBlocks = (theme, saved) => (
  resolveHomeLayout(theme, saved).filter((item) => item.visible).map((item) => item.key)
);

/** Kayıt varsayılandan farklı mı — panelin "Varsayılana dön" düğmesi için. */
export const isDefaultLayout = (theme, blocks) => {
  const catalog = homeBlockCatalog(theme);
  const resolved = resolveHomeLayout(theme, blocks);
  return resolved.every((item, index) => item.visible && item.key === catalog[index]);
};
